/**
 * Which printer(s) a KOT goes to. Port of web `printHelper.js` `getPrintersForTicket` and its
 * helpers (:226-324) — the canonical copy.
 *
 * ONE routing function for every path. v1 had three: printHelper's (with the default-KOT-group
 * fallback), web `polling.js`'s and mobile's (both WITHOUT it), so a ticket whose group was not
 * configured printed from a direct save and was silently dropped by the poller and on mobile.
 */
import type { PrintContext, PrinterRecord } from "./types";
import { isEmpty } from "./util";

/** Ticket-group printer ids that name a slot rather than a saved printer. */
export const DEFAULT_PRINTER_ID = "defaultPrinter";
export const USB_PRINTER_ID = "usb-printer";

/** No resource group on the service label. v1 compares against this literal, never undefined. */
const NO_RESOURCE_GROUP = "noresourcegroup";

export interface RoutedPrinter {
  printer: PrinterRecord;
  /** absent for the default-printer fallback, which v1 prints once */
  copies?: number;
}

/** v1 `getPrinterById`: a slot id, or a saved/local/BLE printer by `mid` (USB is a slot, not a mid). */
export function printerById(printerId: string | undefined, ctx: PrintContext): PrinterRecord {
  if (printerId === DEFAULT_PRINTER_ID) return ctx.printers.defaultPrinter || {};
  if (printerId === USB_PRINTER_ID) return ctx.printers.usb || {};
  const byMid: Record<string, PrinterRecord> = {};
  for (const printer of [...ctx.printers.saved, ...ctx.printers.local, ...ctx.printers.ble]) {
    byMid[printer.mid as string] = printer; // last wins, as v1's arrayToMap
  }
  return (printerId !== undefined && byMid[printerId]) || {};
}

function skipsTicket(groupPrinter: any, resourceGroupId: string, ticket: any): boolean {
  const wrongResourceGroup =
    !!groupPrinter?.resourceGroupIds?.length && !groupPrinter.resourceGroupIds.includes(resourceGroupId);
  const wrongItemType =
    (groupPrinter?.itemType === "Regular" && ticket?.isBackOrderTicket) ||
    (groupPrinter?.itemType === "Back Order" && !ticket?.isBackOrderTicket);
  return wrongResourceGroup || !!wrongItemType;
}

function groupPrinters(printers: any[], resourceGroupId: string, ticket: any, ctx: PrintContext): RoutedPrinter[] {
  const routed: RoutedPrinter[] = [];
  for (const groupPrinter of printers) {
    if (skipsTicket(groupPrinter, resourceGroupId, ticket)) continue;
    const printer = printerById(groupPrinter?.printerId, ctx);
    if (!isEmpty(printer)) routed.push({ printer, copies: groupPrinter?.copies || 1 });
  }
  return routed;
}

/**
 * The printers one prepared ticket prints on, with copies each. `[]` means "no printer" — v1
 * returned `undefined` or a caught Error for the same cases, and its callers read both as none.
 */
export function ticketPrinters(ticket: any, ctx: PrintContext): RoutedPrinter[] {
  const settings = ctx.posSettings?.tickets;
  if (!settings || isEmpty(settings)) return [];

  const resourceGroupId = ticket?.serviceLabel?.resourceGroupId || NO_RESOURCE_GROUP;

  if (!settings.groups?.length) {
    const printer = ctx.printers.defaultPrinter || {};
    return isEmpty(printer) ? [] : [{ printer }];
  }
  if (!ticket?.groupName) return [];

  let routed: RoutedPrinter[] = [];
  for (const group of settings.groups) {
    if (group?.name === ticket.groupName && group?.printers?.length) {
      routed = routed.concat(groupPrinters(group.printers, resourceGroupId, ticket, ctx));
    }
  }
  if (routed.length) return routed;

  // The group is not configured — or every printer in it was filtered out. v1 falls back to the
  // group marked default, then to the default printer.
  const defaultGroup = settings.groups.find((group: any) => group?.defaultKOTGroup === true);
  if (defaultGroup?.printers?.length) return groupPrinters(defaultGroup.printers, resourceGroupId, ticket, ctx);
  const printer = printerById(DEFAULT_PRINTER_ID, ctx);
  return isEmpty(printer) ? [] : [{ printer }];
}

/**
 * Every printer added on this device, deduplicated by `mid`. Port of web `printHelper.js`
 * `getAllAddedPrintersList` (:806) — network, Bluetooth, the one USB printer, then the OS-spooler
 * printers, in that order. A later duplicate replaces the earlier object but keeps its position,
 * which is what v1's `getUniqueListByKey` Map does.
 *
 * Mobile's `getAllAddedLabelPrinters` looked at network + BLE only until 2026-09-15, when it gained
 * the USB printer; it still has no OS-spooler list (`localPrinters` is web-only).
 */
export function addedPrinters(ctx: PrintContext): PrinterRecord[] {
  const all = [...ctx.printers.saved, ...ctx.printers.ble];
  if (ctx.printers.usb?.mid) all.push(ctx.printers.usb);
  all.push(...ctx.printers.local);
  const byMid = new Map<unknown, PrinterRecord>();
  for (const printer of all) byMid.set(printer?.mid, printer);
  return [...byMid.values()];
}

/**
 * The label printers among them — v1 `getAllAddedLabelPrintersList`. The flag is the STRING
 * `"true"`, as the setup form writes it.
 */
export function labelPrinters(ctx: PrintContext): PrinterRecord[] {
  return addedPrinters(ctx).filter((printer) => printer?.enableLabelPrinter === "true");
}
